import { useState } from 'react';
import { Check, Copy, ExternalLink, Info, Pencil, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { useWorkspace } from '@/contexts/workspace-context';
import { midEllipsis, midEllipsisUrl } from '@/lib/mid-ellipsis';
import { singularUnit, unitTitle } from '@/lib/usage-unit';
import { AccessTokenField } from './access-token-field';

export interface ChannelCommonRowsProps {
  channelId: string;
  canEdit: boolean;
  currentWebhookUrl: string | null;
  currentVerifyToken: string | null;
  setShowUrlDialog: (open: boolean) => void;
  setUrlDialogMode: (mode: 'configure' | 'edit') => void;
  setSwitchToCliDialogOpen: (open: boolean) => void;
  // HMAC signing secret — SEPARATE from the verify token. Shown + rotatable
  // only when a webhook destination is configured.
  currentHmacSecret: string | null;
  onRotateHmac: () => void;
  rotatingHmac: boolean;
}

/**
 * The rows every non-WhatsApp channel body shares (billable usage, access
 * token, channel id, inbound destination, verify token, HMAC). Extracted from
 * the Instagram body for the Facebook body (AIT-621); rendered inside the
 * caller's 200px/value grid.
 */
export function ChannelCommonRows({
  channelId,
  canEdit,
  currentWebhookUrl,
  currentVerifyToken,
  setShowUrlDialog,
  setUrlDialogMode,
  setSwitchToCliDialogOpen,
  currentHmacSecret,
  onRotateHmac,
  rotatingHmac,
}: ChannelCommonRowsProps) {
  const { usage } = useWorkspace();
  const channelUsage = usage?.channels.find((a) => a.channelId === channelId);
  const [copiedChannelId, setCopiedChannelId] = useState(false);
  const [copiedVerifyToken, setCopiedVerifyToken] = useState(false);
  const [copiedHmac, setCopiedHmac] = useState(false);
  const [copiedWebhookUrl, setCopiedWebhookUrl] = useState(false);

  async function copyValue(value: string, setCopied: (v: boolean) => void) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy. Please copy manually.');
    }
  }

  function handleEditUrl() {
    setUrlDialogMode('edit');
    setShowUrlDialog(true);
  }

  function handleSetUrl() {
    setUrlDialogMode('configure');
    setShowUrlDialog(true);
  }

  return (
    <>
      {/* Billable usage (AIT-184: the label reflects the billing count, not
          raw activity; AIT-436: named after the org's own meter) */}
      <span className="flex items-center gap-1 text-muted-foreground">
        Billable {unitTitle(usage?.usageUnit)}
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                type="button"
                aria-label="What counts toward my plan?"
                className="inline-flex items-center justify-center leading-none p-0 text-muted-foreground/60 hover:text-muted-foreground"
              />
            }
          >
            <Info className="size-3.5" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {usage?.usageUnit === 'actions'
              ? 'Actions this channel used this quota period. An action is work your app performs: sending a message, replying to a comment, moderating a comment, or publishing media. Messages you receive are free.'
              : "Messages that count toward your plan this quota period: inbound messages delivered to your endpoint and outbound messages sent. Received messages we couldn't deliver are not billed."}
          </TooltipContent>
        </Tooltip>
      </span>
      <span className="flex flex-wrap items-baseline gap-x-2">
        <span className="font-medium tabular-nums">
          {(channelUsage?.total ?? 0).toLocaleString()}{' '}
          {singularUnit(usage?.usageUnit, channelUsage?.total ?? 0)}
        </span>
      </span>

      {/* Access Token — gateway bearer (hmat_live_…), not the raw Meta token. */}
      <AccessTokenField channelId={channelId} canEdit={canEdit} />

      {/* Channel ID */}
      <span className="flex items-center gap-1 text-muted-foreground">
        <span>Channel ID</span>
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                type="button"
                aria-label="What is a Channel ID?"
                className="inline-flex items-center justify-center leading-none p-0 text-muted-foreground/60 hover:text-muted-foreground"
              />
            }
          >
            <Info className="size-3.5" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            Your HookMyApp Channel ID. Use it with the CLI and when
            contacting support.
          </TooltipContent>
        </Tooltip>
      </span>
      <span className="flex items-center gap-1">
        <span>{channelId}</span>
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={() => copyValue(channelId, setCopiedChannelId)}
          aria-label="Copy Channel ID"
          title="Copy Channel ID"
        >
          {copiedChannelId ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        </Button>
      </span>

      {/* Inbound Destination — same shape as WA */}
      <span className="flex items-center gap-1 text-muted-foreground">
        <span>Inbound Destination</span>
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                type="button"
                aria-label="About inbound destination"
                className="inline-flex items-center justify-center leading-none p-0 text-muted-foreground/60 hover:text-muted-foreground"
              />
            }
          >
            <Info className="size-3.5" />
          </TooltipTrigger>
          <TooltipContent className="block max-w-xs">
            Where inbound events are delivered. Either your own
            webhook URL or the HookMyApp CLI.
          </TooltipContent>
        </Tooltip>
      </span>
      {!currentWebhookUrl ? (
        <span
          role="status"
          aria-label="HookMyAppCLI"
          className="flex items-center gap-2 flex-wrap"
        >
          <span className="text-sm font-medium">HookMyApp CLI</span>
          {canEdit && (
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={handleSetUrl}
              aria-label="Configure webhook URL"
              title="Configure webhook URL"
            >
              <Pencil className="size-3.5" />
            </Button>
          )}
        </span>
      ) : (
        <span className="flex items-center gap-1 min-w-0">
          <span className="font-mono text-xs" title={currentWebhookUrl}>
            {midEllipsisUrl(currentWebhookUrl)}
          </span>
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => copyValue(currentWebhookUrl, setCopiedWebhookUrl)}
            aria-label="Copy webhook URL"
            title="Copy webhook URL"
          >
            {copiedWebhookUrl ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
          </Button>
          {canEdit && (
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={handleEditUrl}
              aria-label="Edit webhook URL"
              title="Edit webhook URL"
            >
              <Pencil className="size-3.5" />
            </Button>
          )}
          {canEdit && (
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => setSwitchToCliDialogOpen(true)}
              aria-label="Switch back to HookMyApp CLI"
              title="Switch back to HookMyApp CLI"
            >
              <RotateCcw className="size-3.5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => window.open(currentWebhookUrl, '_blank')}
            aria-label="Open webhook URL"
            title="Open webhook URL"
          >
            <ExternalLink className="size-3.5" />
          </Button>
        </span>
      )}

      {/* Verify Token (only when webhook URL is set) */}
      {currentWebhookUrl && currentVerifyToken && (
        <>
          <span className="text-muted-foreground">Verify Token</span>
          <span className="flex items-center gap-1">
            <span className="font-mono text-sm" title={currentVerifyToken}>
            {midEllipsis(currentVerifyToken, 4, 4)}
          </span>
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => copyValue(currentVerifyToken, setCopiedVerifyToken)}
              aria-label="Copy verify token"
              title="Copy verify token"
            >
              {copiedVerifyToken ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
            </Button>
          </span>
        </>
      )}

      {/* HMAC — the X-HookMyApp-Signature-256 signing secret. SEPARATE from the
          verify token; shown + rotatable only when a webhook is configured. */}
      {currentWebhookUrl && currentHmacSecret && (
        <>
          <span className="flex items-center gap-1 text-muted-foreground">
            HMAC
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button variant="ghost" size="icon-xs" aria-label="What is the HMAC secret">
                    <Info className="size-3.5" />
                  </Button>
                }
              />
              <TooltipContent className="max-w-xs">
                We sign every forwarded webhook with the header
                X-HookMyApp-Signature-256 = sha256=HMAC-SHA256(this secret, raw
                request body). Recompute it on your endpoint to confirm the
                webhook is from us. This is not the verify token.
              </TooltipContent>
            </Tooltip>
          </span>
          <span className="flex items-center gap-1">
            <span className="font-mono text-sm" title={currentHmacSecret}>
              {midEllipsis(currentHmacSecret, 4, 4)}
            </span>
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => copyValue(currentHmacSecret, setCopiedHmac)}
              aria-label="Copy HMAC secret"
              title="Copy HMAC secret"
            >
              {copiedHmac ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
            </Button>
            {canEdit && (
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={onRotateHmac}
                disabled={rotatingHmac}
                aria-label="Rotate HMAC secret"
                title="Rotate HMAC secret"
              >
                <RotateCcw className="size-3.5" />
              </Button>
            )}
          </span>
        </>
      )}

    </>
  );
}
